import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  closeModal,
  initialQueryState,
  openModal,
  showToastMessage,
  updateQueryStateTable,
  useTableStore,
} from 'enable-ui';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { MODAL_KEY } from 'modules/auth/core/modal-key';

import type { Assessment } from '../type';

import { trimValue } from 'helpers';
import { setUrlParams } from 'helpers/url';
import { useBoolean, useNumber, useString } from 'hooks';
import { apiCall } from 'hooks/service';
import { useUpdateQueryState } from 'hooks/url';
import { cloneDeep, debounce, isUndefined } from 'lodash';

import { SUCCESS_MESSAGE } from 'constants/message';
import { PATH } from 'constants/path';
import { TABLE_KEY } from 'constants/table';

export const useAssessmentsController = () => {
  const navigationTo = useNavigate();

  const params = useParams();

  const { institutionId } = params;

  useUpdateQueryState(TABLE_KEY.ASSESSMENTS, 12);

  const queryState = useTableStore((s) => s.state.queryState[TABLE_KEY.ASSESSMENTS]);

  const userProfile = useAuthStore(userProfileSelector);

  const [assessmentData, setAssessmentData] = useState<Assessment[]>([]);

  const [selectedAssessments, setSelectedAssessments] = useState<Record<string, string>>({});

  const searchAssessment = useString('');

  const assessmentSelectedId = useString('');

  const total = useNumber(0);

  const isDefault = useBoolean(false);

  useEffect(() => {
    if (!userProfile?.id) {
      return;
    }

    if (queryState) {
      setUrlParams(queryState);

      searchAssessment.setValue(queryState.search ?? '');

      getListAssessment();
    }
  }, [JSON.stringify(queryState), userProfile]);

  useEffect(() => {
    updateQueryStateTable(TABLE_KEY.ASSESSMENTS, {
      ...initialQueryState,
      limit: 12,
    });

    return () => {
      updateQueryStateTable(TABLE_KEY.ASSESSMENTS, {
        ...initialQueryState,
        limit: 12,
      });
    };
  }, []);

  const getListAssessment = async () => {
    const result: {
      items: Assessment[];
      total: number;
      isDefault: boolean;
    } = await apiCall({
      url: '/assessments',
      method: 'GET',
      isLoading: true,
      showError: true,
      data: {
        ...queryState,
        institutionId: userProfile?.institution?.id ?? institutionId,
      },
    });

    if (result?.items) {
      setAssessmentData(result?.items ?? []);
    }

    if (result?.total) {
      total.setValue(result.total);
    }

    if (result?.isDefault) {
      isDefault.setValue(result.isDefault);
    }
  };

  const onChangeSelectedAssessments = (assessment: string) => () => {
    const newSelected = cloneDeep(selectedAssessments);

    if (!isUndefined(newSelected[assessment])) {
      delete newSelected[assessment];
    } else {
      newSelected[assessment] = assessment;
    }

    setSelectedAssessments(newSelected);
  };

  const onChangeSearchAssessment = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchAssessment.setValue(event.target.value);
    debounceSearch(event.target.value);
  };

  const debounceSearch = useCallback(
    debounce((newSearch: string) => {
      updateQueryStateTable(TABLE_KEY.ASSESSMENTS, {
        ...queryState,
        search: trimValue(newSearch),
        page: 1,
      });
    }, 500),
    [],
  );

  const goToAssessmentDetail = (assessmentId: string) => {
    navigationTo(PATH.EDIT_ASSESSMENTS.replace(':id', assessmentId));
  };

  const handleEditAssessment = (assessmentId: string) => () => {
    navigationTo(PATH.EDIT_ASSESSMENTS.replace(':id', assessmentId));
  };

  const handleDeleteAssessment = (assessmentId: string) => async () => {
    openModal(MODAL_KEY.DELETE_ASSESSMENT);
    assessmentSelectedId.setValue(assessmentId);
  };

  const callbackFirstPage = () => {
    updateQueryStateTable(TABLE_KEY.ASSESSMENTS, {
      ...initialQueryState,
      page: 1,
      limit: 12,
    });
  };

  const onDeleteAssessment = async () => {
    const response: { id: string } = await apiCall({
      url: `/assessments/${assessmentSelectedId.value}`,
      method: 'DELETE',
      isLoading: true,
      showError: true,
    });

    if (response?.id) {
      callbackFirstPage();
    }

    getListAssessment();
    setSelectedAssessments({});
    closeModal(MODAL_KEY.DELETE_ASSESSMENT);
    showToastMessage('success', SUCCESS_MESSAGE.DELETE_ASSESSMENT);
  };

  const handleDelete = async () => {
    const result: string[] = [];

    for (const el of Object.keys(selectedAssessments)) {
      const response: { id: string } = await apiCall({
        url: `/assessments/${selectedAssessments[el]}`,
        method: 'DELETE',
        isLoading: true,
        showError: true,
      });

      if (response?.id) {
        result.push(response.id);
      }
    }

    if (result.length === Object.keys(selectedAssessments).length) {
      callbackFirstPage();
      showToastMessage('success', SUCCESS_MESSAGE.DELETE_ASSESSMENT);
    }

    setSelectedAssessments({});
    getListAssessment();
  };

  const handleDuplicateAssessment = (assessmentId: string) => async () => {
    const result: Assessment = await apiCall({
      url: `/assessments/${assessmentId}/duplicate`,
      method: 'POST',
      isLoading: true,
      showError: true,
    });

    if (!result?.id) {
      return;
    }

    showToastMessage('success', 'Course duplicated successfully!');

    getListAssessment();
  };

  return {
    assessmentData,
    total,
    queryState,
    selectedAssessments,
    setSelectedAssessments,
    onChangeSelectedAssessments,
    searchAssessment,
    onChangeSearchAssessment,
    goToAssessmentDetail,
    handleDeleteAssessment,
    onDeleteAssessment,
    handleDelete,
    handleDuplicateAssessment,
    handleEditAssessment,
    isDefault: isDefault.value,
  };
};
